import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { adminApi, errMsg } from "@/lib/api";
import { Field, Loading, PageHead, Panel, SaveBtn, inputCls, useSiteDraft } from "@/admin/ui";

const img = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const areaCls = `${inputCls} h-auto py-2 font-arial leading-relaxed`;

function PhotoUpload({ slot, url, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      onUploaded((await adminApi.post(`/branding/about-${slot}`, fd)).data.url);
      toast.success("Photo updated on the website");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="flex items-end gap-4">
      <div className="w-28 aspect-[4/5] bg-zinc-100 border border-zinc-200 overflow-hidden">{url && <img src={img(url)} alt="" className="w-full h-full object-cover" data-testid={`about-admin-photo-${slot}`} />}</div>
      <label className={`inline-flex items-center gap-2 h-9 px-4 text-xs font-bold bg-black text-white cursor-pointer hover:bg-red transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}>
        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Change photo"}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`about-admin-photo-input-${slot}`} />
      </label>
    </div>
  );
}

export default function AboutAdmin() {
  const { draft, patch, save, saving } = useSiteDraft(["about"]);
  if (!draft) return <Loading />;
  const a = draft.about;
  const set = (k, v) => patch("about", { ...a, [k]: v });
  const txt = (k, label, rows) => (
    <Field label={label}>
      {rows ? <textarea rows={rows} value={a[k]} onChange={(e) => set(k, e.target.value)} className={areaCls} data-testid={`about-admin-${k}`} />
        : <input value={a[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`about-admin-${k}`} />}
    </Field>
  );
  const setList = (k, i, f, v) => set(k, a[k].map((x, n) => (n === i ? { ...x, [f]: v } : x)));
  return (
    <div className="max-w-3xl space-y-6">
      <PageHead title="About us page" desc="Edit the text and photos on /about-us. Leave a blank line between paragraphs.">
        <SaveBtn save={save} saving={saving} />
      </PageHead>
      <Panel className="grid gap-4">{txt("quote", "Opening quote")}{txt("intro", "Our story", 14)}</Panel>
      <Panel className="grid sm:grid-cols-3 gap-4">
        {a.stats.map((s, i) => (
          <div key={i} className="grid gap-2">
            <Field label={`Highlight ${i + 1}`}><input value={s.value} onChange={(e) => setList("stats", i, "value", e.target.value)} className={inputCls} data-testid={`about-admin-stat-value-${i}`} /></Field>
            <input value={s.label} onChange={(e) => setList("stats", i, "label", e.target.value)} className={inputCls} data-testid={`about-admin-stat-label-${i}`} />
          </div>
        ))}
      </Panel>
      <Panel className="grid gap-4">
        {txt("team_label", "Team label")}{txt("team_heading", "Team heading")}{txt("team_intro", "Team intro", 3)}
        {a.people.map((p, i) => (
          <div key={i} className="grid gap-3 border-t border-zinc-200 pt-4" data-testid={`about-admin-person-${i}`}>
            <Field label={`Person ${i + 1} name`}><input value={p.name} onChange={(e) => setList("people", i, "name", e.target.value)} className={inputCls} data-testid={`about-admin-person-name-${i}`} /></Field>
            <PhotoUpload slot={i + 1} url={p.image} onUploaded={(u) => setList("people", i, "image", u)} />
            <Field label="Text"><textarea rows={5} value={p.text} onChange={(e) => setList("people", i, "text", e.target.value)} className={areaCls} data-testid={`about-admin-person-text-${i}`} /></Field>
          </div>
        ))}
      </Panel>
      <Panel className="grid gap-4">{txt("why_label", "Why vinyl label")}{txt("why_heading", "Why vinyl heading")}{txt("why_body", "Why vinyl text", 10)}</Panel>
      <Panel className="grid sm:grid-cols-2 gap-4">{txt("cta_heading", "Bottom heading")}{txt("cta_button", "Button text")}</Panel>
    </div>
  );
}
